import { XMarkIcon } from "@heroicons/react/20/solid";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { suggestGroupMembers } from "../../../api/access/accessApi";
import type { AccessList, ListedGroup } from "../../../api/access/accessModel";
import AppButton from "../../../ui/buttons/AppButton";
import IconButton from "../../../ui/buttons/IconButton";
import PersonAvatar from "../../../ui/avatars/PersonAvatar";
import EmptyState from "../../../ui/feedback/EmptyState";
import ListCard from "../../../ui/layout/ListCard";
import { memberAdded, memberRemoved } from "../changes/accessChanges";
import type { AccessChanges } from "../changes/useAccessChanges";
import { viaGroups } from "../model/groupOrder";
import type { GroupNamer } from "../model/groupNames";
import PersonSuggestField from "./PersonSuggestField";

interface GroupMembersProps {
  group: ListedGroup;
  list: AccessList;
  namer: GroupNamer;
  changes: AccessChanges;
}

/**
 * Who is in a group: its own members, and those it reaches through a group inside it ("via
 * Student assistants"). Whom the server lets change its members adds people from the account
 * suggestions and takes them out (`membersManageable`, `removableMemberIds`); each change
 * applies at once, with Undo. An installation group's members are the installation's (PV3-6).
 */
export default function GroupMembers({ group, list, namer, changes }: GroupMembersProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const mayChange = group.membersManageable;
  const suggest = useMemo(() => (query: string, signal: AbortSignal) => suggestGroupMembers(group.groupId, query, signal), [group.groupId]);
  const named = { groupId: group.groupId, name: namer.name(group.groupId) };
  const personName = (userId: string) => list.namedPeople.get(userId)?.displayName ?? "";
  const people = [...group.peopleIds].sort((left, right) => personName(left).localeCompare(personName(right)));
  // Taking someone out needs their highest group below the viewer's too (GS-3), as the server says per group.
  const mayTakeOut = new Set(group.removableMemberIds);
  const elsewhere = !mayChange && group.projectId === null && list.projectId !== null;

  return (
    <ListCard title={t("access.members")} actions={mayChange ? (
      <AppButton variant="secondary" size="sm" aria-expanded={adding} onClick={() => { setAdding(!adding); }}>{t("access.add")}</AppButton>
    ) : undefined}>
      {adding ? (
        <div className="px-4 pb-3">
          <PersonSuggestField label={t("access.addTo", { group: named.name })} suggest={suggest} clearOnChoose={true} autoFocus={true}
            onChoose={(suggestion) => { changes.run(memberAdded(t, named, { userId: suggestion.userId, name: suggestion.displayName })); }} />
        </div>
      ) : null}
      {group.system === "everyone" ? <p className="app-text-muted px-4 pb-3 text-sm">{t("access.everyoneWithAccount")}</p> : people.length === 0 ? (
        <EmptyState size="small" className="px-4 pb-3">{t("access.noMembers")}</EmptyState>
      ) : (
        <ul className="app-divide-y text-sm">
          {people.map((userId) => {
            const own = group.memberIds.includes(userId);
            const inner = own ? null : viaGroups(list.people, userId, group.groupId);
            return (
              <li key={userId} className="flex items-center gap-2 px-4 py-2">
                <PersonAvatar userId={userId} name={personName(userId)} size="xs" />
                <span className="min-w-0 flex-1 truncate">{personName(userId)}</span>
                {inner === null ? null : <span className="app-text-muted text-xs">{t("access.via", { group: namer.path(inner) })}</span>}
                {mayTakeOut.has(userId) ? (
                  <IconButton label={t("access.takeOut", { name: personName(userId), group: named.name })} disabled={changes.busy}
                    onClick={() => { changes.run(memberRemoved(t, named, { userId, name: personName(userId) })); }}>
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </IconButton>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {elsewhere ? <p className="app-text-muted app-divider-top px-4 py-2 text-xs">{t("access.membersInAdministration")}</p> : null}
    </ListCard>
  );
}
